// App store module - session and user state
import { progressFetch } from '../helpers/progress-fetch.js'
import PageSync from '../helpers/page-sync.js'
import { isTestEnv } from '../helpers/env.js'

export default {
  namespaced: true,

  state: {
    user: null,
    true_user: null,
    simulating: false,
    permissions: {},
    locale: 'de',
    csrfToken: null,
    links: {},
    loading: false,
    initialized: false
  },

  getters: {
    isAuthenticated: state => state.user !== null,

    isAdmin: state => state.permissions.is_admin === true,

    isSts: state => state.permissions.is_sts === true,

    isAssembler: state => state.permissions.is_assembler === true,

    isCustomer: state => state.permissions.is_customer === true
  },

  mutations: {
    setSession(state, data) {
      state.user = data.user
      state.true_user = data.true_user || null
      state.simulating = data.simulating || false
      state.permissions = data.permissions || {}
      state.locale = data.locale || 'de'
      state.csrfToken = data.csrf_token
      state.links = data.links || {}
      state.initialized = true
    },

    setLoading(state, loading) {
      state.loading = loading
    }
  },

  actions: {
    async fetchSession({ commit }) {
      commit('setLoading', true)

      try {
        const response = await progressFetch('/api/session', {
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin'
        })

        if (!response.ok) {
          throw new Error('Session fetch failed')
        }

        const data = await response.json()
        commit('setSession', data)

        // Subscribe to current user updates via PageSync
        if (data.user?.id) {
          PageSync.register(`/users/${data.user.id}`, true)
          if (isTestEnv()) {
            document.body.dataset.pageSyncUserSubscribed = 'true'
          }
        }
        // Subscribe to initial route
        PageSync.subscribe(document.location.pathname)
      } catch (error) {
        console.error('Session fetch error:', error)
        // Set initialized so auth guard can redirect to login
        commit('setSession', { user: null, csrf_token: null, locale: 'de' })
      } finally {
        commit('setLoading', false)
      }
    },

    async logout({ state, commit, dispatch }) {
      const { csrfToken } = state

      // Reset all stores to clear user data
      dispatch('resetAllStores')

      try {
        const response = await fetch('/api/account/sign_out', {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken
          },
          credentials: 'same-origin'
        })

        if (!response.ok) {
          throw new Error('Logout failed')
        }

        // Fetch fresh session (will have no user)
        await dispatch('fetchSession')
      } catch (error) {
        console.error('Logout error:', error)
        // Clear session anyway
        commit('setSession', { user: null, csrf_token: null, locale: 'de' })
      }
    },

    async impersonate({ state, dispatch }, userId) {
      const response = await fetch(`/api/users/${userId}/simulate`, {
        method: 'GET',
        headers: { 'X-CSRF-Token': state.csrfToken },
        credentials: 'same-origin'
      })
      if (!response.ok) throw new Error('Impersonate failed')
      dispatch('resetAllStores')
      await dispatch('fetchSession')
    },

    async stopImpersonating({ state, dispatch }) {
      const response = await fetch('/api/users/stop_simulating', {
        method: 'GET',
        headers: { 'X-CSRF-Token': state.csrfToken },
        credentials: 'same-origin'
      })
      if (!response.ok) throw new Error('Stop impersonating failed')
      dispatch('resetAllStores')
      await dispatch('fetchSession')
    },

    resetAllStores({ commit, rootState }) {
      // List of modules with resetState mutations
      const modulesWithReset = [
        'projects', 'products', 'partners', 'users', 'tasks',
        'project', 'product', 'taskDetail', 'folders', 'messages',
        'lineItems', 'userForm', 'partnerForm', 'attachmentBrowser', 'feedback'
      ]

      // Reset each registered module
      modulesWithReset.forEach(moduleName => {
        if (rootState[moduleName]) {
          commit(`${moduleName}/resetState`, null, { root: true })
        }
      })

      // Clear flash messages
      if (rootState.flash) {
        commit('flash/clear', null, { root: true })
      }

      // Hide confirm dialog
      if (rootState.confirm) {
        commit('confirm/hide', null, { root: true })
      }
    }
  }
}
